// Page/device context sent with each captured request (franklycatering #497).

export type DeviceType = "mobile" | "tablet" | "desktop";

export function classifyDevice(width: number): DeviceType {
  if (width < 768) return "mobile";
  if (width < 1280) return "tablet";
  return "desktop";
}

export interface RequestContext {
  page_path: string;
  page_url: string;
  viewport: { width: number; height: number; dpr: number };
  device_type: DeviceType;
  dialog_title: string | null;
  user_agent: string;
}

const MAX_DIALOG_TITLE = 200;

/**
 * Resolves the topmost open dialog's title. Queries both `[role="dialog"]`
 * and `[role="alertdialog"]` with `data-state="open"`, takes the LAST match
 * (topmost when multiple are stacked), and resolves aria-labelledby before
 * aria-label.
 */
export function openDialogTitle(): string | null {
  const dialogs = document.querySelectorAll(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  );
  if (dialogs.length === 0) return null;
  const top = dialogs[dialogs.length - 1];

  let raw: string | null = null;
  const labelledBy = top.getAttribute("aria-labelledby");
  if (labelledBy) {
    raw = document.getElementById(labelledBy)?.textContent ?? null;
  }
  if (!raw) raw = top.getAttribute("aria-label");
  if (!raw) return null;

  const collapsed = raw.replace(/\s+/g, " ").trim();
  return collapsed ? collapsed.slice(0, MAX_DIALOG_TITLE) : null;
}

export function collectRequestContext(): RequestContext {
  return {
    page_path: window.location.pathname + window.location.search,
    page_url: window.location.href,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      dpr: window.devicePixelRatio || 1,
    },
    device_type: classifyDevice(window.innerWidth),
    dialog_title: openDialogTitle(),
    user_agent: navigator.userAgent,
  };
}
